import React, { useState, Fragment, useRef } from "react";
import {
  Table,
  Row,
  Col,
  Card,
  CardBody,
  Spinner,
  Input,
  Label,
} from "reactstrap";
import Flatpickr from "react-flatpickr";
import "flatpickr/dist/themes/material_blue.css";
import {
  useReactTable,
  getCoreRowModel,
  getSortedRowModel,
  getFilteredRowModel,
  flexRender,
} from "@tanstack/react-table";
import { showToast } from "../ToastNotification";
import CommonLoading from "./CommonLoading";
import {
  formatDate,
  getCurrentFiscalYearDates,
  getQuickRangeDates,
} from "./WorkingDateRange";
import { useVirtualizer } from "@tanstack/react-virtual";

const TableContainerDateRangeFilter = ({
  footer,
  summary,
  columns,
  data,
  theadClass = "",
  tbodyClass = "",
  onDateRangeChange,
  setDateRange,
  dateRange,
  exchangeFilter,
  setExchangeFilter,
  fromDate,
  setFromDate,
  toDate,
  setToDate,
}) => {
  const [globalFilter, setGlobalFilter] = useState("");
  const [sorting, setSorting] = useState([]);
  const { fyStart, fyEnd } = getCurrentFiscalYearDates();

  const [tableLoading, setTableLoading] = useState(false);

  const convertToDateFormat = (dateString) => {
    const months = {
      January: "01",
      February: "02",
      March: "03",
      April: "04",
      May: "05",
      June: "06",
      July: "07",
      August: "08",
      September: "09",
      October: "10",
      November: "11",
      December: "12",
    };

    const [day, month, year] = dateString.replace(",", "").split(" ");
    return `${year}-${months[month]}-${day.padStart(2, "0")}`;
  };

  // states for options for date filter
  const [selectedRange, setSelectedRange] = useState("this_month"); // default Year Till Date

  const dateOptions = [
    { label: "Today", value: "today" },
    { label: "Last 7 Days", value: "last_7_days" },
    { label: "Last 15 Days", value: "last_15_days" },
    { label: "Last 30 Days", value: "last_30_days" },
    { label: "Year Till Date", value: "this_month" },
    { label: "Custom Date", value: "custom" },
  ];

  const table = useReactTable({
    columns,
    data,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    state: {
      globalFilter,
      sorting,
    },
    onGlobalFilterChange: setGlobalFilter,
    onSortingChange: setSorting,
  });

  // const initialRange =
  //   summary?.start && summary?.end
  //     ? [convertToDateFormat(summary?.start), convertToDateFormat(summary?.end)]
  //     : [];

  const initialRange =
    summary?.start && summary?.end
      ? [new Date(summary.start), new Date(summary.end)]
      : [];

  // console.log("initialRange", initialRange);

  const handleApplyClick = () => {
    setTableLoading(true);

    if (selectedRange !== "custom") {
      const { start, end, formattedStart, formattedEnd } =
        getQuickRangeDates(selectedRange);

      setDateRange({ start_date: formattedStart, end_date: formattedEnd });
      setFromDate(start);
      setToDate(end);

      return onDateRangeChange({
        start_date: formattedStart,
        end_date: formattedEnd,
        type: exchangeFilter,
        setTableLoading,
      });
    }

    // CUSTOM → use fromDate / toDate
    onDateRangeChange({
      start_date: dateRange?.start_date,
      end_date: dateRange?.end_date,
      type: exchangeFilter,
      setTableLoading,
    });
  };

  const isNumeric = (value) => {
    if (typeof value === "number") return true;
    if (typeof value === "string") {
      // Remove commas before testing.
      return !isNaN(Number(value.replace(/,/g, "")));
    }
    return false;
  };

  const tableContainerRef = useRef(null);

  const rows = table.getRowModel().rows;

  // Virtual row configuration
  const rowVirtualizer = useVirtualizer({
    count: rows.length,
    getScrollElement: () => tableContainerRef.current,
    estimateSize: () => 45, // Estimated row height in pixels
    overscan: 10, // Number of items to render outside visible area
  });

  const virtualRows = rowVirtualizer.getVirtualItems();
  const totalSize = rowVirtualizer.getTotalSize();

  const paddingTop = virtualRows.length > 0 ? virtualRows[0].start : 0;
  const paddingBottom =
    virtualRows.length > 0
      ? totalSize - virtualRows[virtualRows.length - 1].end
      : 0;

  return (
    <Fragment>
      {/* Search and Date Filter */}
      <Row className="mb-2">
        <Col xl="5" xxl="4" lg="7" md="12" xs="12" className="mb-2 mb-xxl-0">
          <div className="d-flex w-100 flex-wrap">
            <Label
              htmlFor="type"
              className="form-label d-flex align-items-center  m-0 me-2 text-dark fs-14 fw-medium"
            >
              Select Report Type:
            </Label>
            <div className="search-box flex-grow-1">
              <Input
                type="select"
                id="type"
                name="type"
                className="form-control w-100"
                style={{
                  backgroundColor: "#F1F4F7",
                  border: "1px solid transparent",
                  height: "38px",

                  cursor: "pointer",
                }}
                value={exchangeFilter}
                onChange={(e) => setExchangeFilter(e.target.value)}
              >
                <option value="with-mtf">With MTF</option>
                <option value="without-mtf">Without MTF</option>
                <option value="mtf-only">MTF Only</option>
              </Input>
            </div>
          </div>
        </Col>
        <Col
          xl={selectedRange === "custom" ? "6" : "4"}
          xxl={selectedRange === "custom" ? "6" : "2"}
          lg={selectedRange === "custom" ? "7" : "7"}
          md={selectedRange === "custom" ? "7" : "12"}
          sm="12"
          className="mb-2 mb-xxl-0"
        >
          <div
            className="flex-shrink-0 flex-grow-1"
            style={{ minWidth: "150px" }}
          >
            <Input
              type="select"
              id="type"
              name="type"
              className="form-control w-100"
              style={{
                backgroundColor: "#F1F4F7",
                border: "1px solid transparent",
                height: "38px",

                cursor: "pointer",
              }}
              value={selectedRange}
              onChange={(e) => {
                const dateValue = e.target.value;
                setSelectedRange(dateValue);

                if (dateValue !== "custom") {
                  // Auto-fill dates for quick range
                  const { start, end, formattedStart, formattedEnd } =
                    getQuickRangeDates(dateValue);
                  setDateRange({
                    start_date: formattedStart,
                    end_date: formattedEnd,
                  });
                  setFromDate(start);
                  setToDate(end);
                }
              }}
            >
              {dateOptions.map((opt, idx) => (
                <option key={idx} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </Input>
          </div>
          {selectedRange === "custom" && (
            <>
              <div className="d-flex align-items-center gap-2 flex-fill mt-2 flex-wrap">
                <div className="d-flex align-items-center gap-2 flex-grow-1">
                  <Label
                    htmlFor="type"
                    className="form-label d-flex align-items-center m-0  text-dark fs-14 fw-medium"
                  >
                    From:
                  </Label>

                  <div className="position-relative w-100">
                    <Flatpickr
                      style={{
                        backgroundColor: "#F1F4F7",
                        border: "1px solid transparent",

                        paddingRight: "30px",
                      }}
                      options={{
                        dateFormat: "d-m-Y",
                        minDate: fyStart,
                        maxDate: new Date(
                          Math.min(
                            fyEnd,
                            new Date(new Date().setDate(new Date().getDate()))
                          )
                        ),
                      }}
                      value={fromDate}
                      onChange={(dates) => {
                        const selected = dates[0];
                        setFromDate(selected);

                        // FIX: update dateRange
                        setDateRange((prev) => ({
                          ...prev,
                          start_date: formatDate(selected),
                        }));

                        if (
                          toDate &&
                          selected &&
                          toDate.getTime() < selected.getTime()
                        ) {
                          setToDate(null);
                        }
                      }}
                      className="form-control"
                      placeholder="dd-mm-yyy"
                    />
                    <i className="bx bx-calendar d-flex h-100 align-items-center position-absolute date_range_icon"></i>
                  </div>
                </div>
                <div className="d-flex align-items-center gap-2 flex-grow-1">
                  <Label
                    htmlFor="type"
                    className="form-label d-flex align-items-center m-0  text-dark fs-14 fw-medium"
                  >
                    To:
                  </Label>
                  <div className="position-relative w-100">
                    <Flatpickr
                      style={{
                        backgroundColor: "#F1F4F7",
                        border: "1px solid transparent",
                        paddingRight: "30px",
                      }}
                      options={{
                        dateFormat: "d-m-Y",
                        minDate: fromDate || fyStart,
                        maxDate: new Date(
                          Math.min(
                            fyEnd,
                            new Date(new Date().setDate(new Date().getDate()))
                          )
                        ),
                      }}
                      value={toDate}
                      onChange={(dates) => {
                        const selected = dates[0];
                        setToDate(selected);

                        // FIX: update dateRange
                        setDateRange((prev) => ({
                          ...prev,
                          end_date: formatDate(selected),
                        }));
                      }}
                      className="form-control"
                      placeholder="dd-mm-yyyy"
                    />

                    <i className="bx bx-calendar d-flex h-100 align-items-center position-absolute date_range_icon"></i>
                  </div>
                </div>
              </div>
            </>
          )}
        </Col>
        <Col xl="2" xxl="1" lg="2" md="3" sm="4">
          <div className="">
            <button
              className="btn btn-primary w-100"
              onClick={handleApplyClick}
              disabled={tableLoading}
            >
              Apply
            </button>
          </div>
        </Col>
      </Row>
      {!tableLoading && !footer && (
        <Row className="mb-2 border-top border-bottom m-0 p-0 py-1">
          <Col xxl={3} xl={3} lg={3} md={6} sm={6} xs={6}>
            <div className="d-block d-sm-flex flex-column centerContent p-2">
              <p className="text-uppercase fs-p-sm mb-1 text-muted fw-semibold">
                Opening Balance
              </p>
              <h4 className="mb-0 text-black">
                ₹
                {parseFloat(summary?.opening_balance ?? 0).toLocaleString(
                  "en-IN"
                )}
              </h4>
            </div>
          </Col>
          <Col xxl={3} xl={3} lg={3} md={6} sm={6} xs={6}>
            <div className="d-block d-sm-flex flex-column centerContent p-2">
              <p className="text-uppercase fs-p-sm mb-1 text-muted fw-semibold">
                Total Credit
              </p>
              <h4 className="mb-0 text-black">
                ₹
                {parseFloat(summary?.total_credit ?? 0).toLocaleString("en-IN")}
              </h4>
            </div>
          </Col>
          <Col xxl={3} xl={3} lg={3} md={6} sm={6} xs={6}>
            <div className="d-block d-sm-flex flex-column centerContent p-2">
              <p className="text-uppercase fs-p-sm mb-1 text-muted fw-semibold">
                Total Debit
              </p>
              <h4 className="mb-0 text-black">
                ₹{parseFloat(summary?.total_debit ?? 0).toLocaleString("en-IN")}
              </h4>
            </div>
          </Col>
          <Col xxl={3} xl={3} lg={3} md={6} sm={6} xs={6}>
            <div className="d-block d-sm-flex flex-column centerContent p-2">
              <p className="text-uppercase fs-p-sm mb-1 text-muted fw-semibold">
                Closing Balance
              </p>
              <h4 className="mb-0 text-black">
                ₹
                {parseFloat(summary?.closing_balance ?? 0).toLocaleString(
                  "en-IN"
                )}
              </h4>
            </div>
          </Col>
        </Row>
      )}

      {/* Table Container */}
      {/* <div className="fs-14 fw-semibold text-dark mb-2">
        <span className="fs-20 fw-semibold text-danger">*</span> MTF Record
      </div> */}
      <div className="table-responsive table-container">
        <div
          ref={tableContainerRef}
          style={{
            maxHeight: "65vh",
            overflow: "auto",
            position: "relative",
          }}
        >
          <table className="table table-hover align-middle table-nowrap table-bordered">
            <thead
              className={`bg-gl-primary sticky-header ${theadClass}`}
              style={{
                position: "sticky",
                top: -1,
                zIndex: 2,
                background: "inherit",
              }}
            >
              {table.getHeaderGroups().map((headerGroup) => (
                <tr key={headerGroup.id}>
                  {headerGroup.headers.map((header) => {
                    const isSorted = header.column.getIsSorted();
                    return (
                      <th
                        key={header.id}
                        style={{
                          textAlign: header.column.columnDef.headerTextAlign,
                          cursor: "pointer",
                        }}
                        // onClick={header.column.getToggleSortingHandler()}
                      >
                        <div className="d-flex align-items-center justify-content-between">
                          {flexRender(
                            header.column.columnDef.Header,
                            header.getContext()
                          )}
                          {/* {isSorted === "asc" ? (
                          <i className="ms-2 bx bx-caret-up"></i>
                        ) : isSorted === "desc" ? (
                          <i className="ms-2 bx bx-caret-down"></i>
                        ) : (
                          <i className="ms-2 bx bxs-sort-alt"></i>
                        )} */}
                        </div>
                      </th>
                    );
                  })}
                </tr>
              ))}
            </thead>
            {tableLoading ? (
              <tr>
                <td
                  colSpan={columns?.length}
                  className="p-1"
                  style={{ textAlign: "center" }}
                >
                  <Card className="m-0 text-muted">
                    <CardBody className="centerContent">
                      <CommonLoading
                        style={{
                          maxHeight: "6vh",
                        }}
                      />
                    </CardBody>
                  </Card>
                </td>
              </tr>
            ) : (
              <>
                <tbody className={tbodyClass}>
                  {data?.length > 0 ? (
                    rows.length > 0 ? (
                      <>
                        {paddingTop > 0 && (
                          <tr>
                            <td
                              colSpan={columns?.length}
                              style={{ height: `${paddingTop}px`, padding: 0 }}
                            />
                          </tr>
                        )}
                        {virtualRows.map((virtualRow) => {
                          const row = rows[virtualRow.index];
                          return (
                            <tr
                              key={row.id}
                              style={
                                row.original.isGroupSummary
                                  ? {
                                      backgroundColor: "#F1F4F7",
                                      borderColor: "#F1F4F7",
                                    }
                                  : {}
                              }
                              className={
                                row.original.isGroupSummary
                                  ? "group-summary"
                                  : ""
                              }
                            >
                              {row.getVisibleCells().map((cell) => {
                                // Retrieve the raw value from the cell.
                                const rawValue = cell.getValue();
                                // Render the cell using its predefined renderer.
                                let renderedContent = flexRender(
                                  cell.column.columnDef.cell,
                                  cell.getContext()
                                );

                                // If the cell is not from the 'running_balance' column and its raw value is numeric,
                                // override the rendered content with the formatted string.
                                if (
                                  cell.column.id !== "running_balance" &&
                                  isNumeric(rawValue)
                                ) {
                                  const numericValue = Number(
                                    String(rawValue).replace(/,/g, "")
                                  );
                                  renderedContent = numericValue.toLocaleString(
                                    "en-IN",
                                    {
                                      minimumFractionDigits: 2,
                                      maximumFractionDigits: 2,
                                    }
                                  );
                                } else if (
                                  !React.isValidElement(renderedContent) &&
                                  isNumeric(rawValue)
                                ) {
                                  // In case the rendered output isn't a React element but a number/string,
                                  // ensure formatting as well.
                                  const numericValue = Number(
                                    String(rawValue).replace(/,/g, "")
                                  );
                                  renderedContent = numericValue.toLocaleString(
                                    "en-IN",
                                    {
                                      minimumFractionDigits: 2,
                                      maximumFractionDigits: 2,
                                    }
                                  );
                                }

                                return (
                                  <td
                                    key={cell.id}
                                    className="fs-p-bg fw-medium"
                                    style={{
                                      textAlign:
                                        cell.column.columnDef.bodyTextAlign,
                                      // whiteSpace: "normal",
                                      padding: "0.3rem",
                                      paddingLeft: "0.4rem",
                                    }}
                                  >
                                    {renderedContent}
                                  </td>
                                );
                              })}
                            </tr>
                          );
                        })}
                        {paddingBottom > 0 && (
                          <tr>
                            <td
                              colSpan={columns?.length}
                              style={{
                                height: `${paddingBottom}px`,
                                padding: 0,
                              }}
                            />
                          </tr>
                        )}
                      </>
                    ) : (
                      <tr>
                        <td
                          colSpan={columns?.length}
                          className="p-1"
                          style={{ textAlign: "center" }}
                        >
                          <Card className="m-0 text-muted">
                            <CardBody>No Record Found</CardBody>
                          </Card>
                        </td>
                      </tr>
                    )
                  ) : (
                    <tr>
                      <td
                        colSpan={columns?.length}
                        className="p-1"
                        style={{ textAlign: "center" }}
                      >
                        <Card className="m-0 text-muted">
                          <CardBody>No Record Found</CardBody>
                        </Card>
                      </td>
                    </tr>
                  )}
                </tbody>
                {footer && (
                  <tfoot className="table-footer sticky-footer">
                    <tr className="bg-gl-success">
                      <th colSpan={4} style={{ textAlign: "end" }}>
                        Total
                      </th>
                      <th style={{ textAlign: "end" }}>
                        {parseFloat(summary?.total_debit).toLocaleString(
                          "en-IN"
                        )}
                      </th>
                      <th style={{ textAlign: "end" }}>
                        {parseFloat(summary?.total_credit).toLocaleString(
                          "en-IN"
                        )}
                      </th>
                      <th style={{ textAlign: "end" }}>
                        {parseFloat(summary?.total_jobbing).toLocaleString(
                          "en-IN"
                        )}
                      </th>
                    </tr>
                  </tfoot>
                )}
              </>
            )}
          </table>
        </div>
      </div>
    </Fragment>
  );
};

export default TableContainerDateRangeFilter;
